import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Icon from "@/components/Icon";
import { Reveal, Eyebrow, EASE } from "@/components/Reveal";
import { useLang } from "@/i18n";

const FAQS = [
    {
        q: "What products and services do you offer?",
        a: "We offer Smarten inverters, inverter batteries, lithium batteries, solar panels and complete solar setups, all types of CCTV cameras with installation, and broadband connections for homes and businesses — along with installation and support assistance.",
    },
    {
        q: "How much does a product or setup cost?",
        a: "Pricing depends on the model, capacity and your exact requirement. Call us on 9933719744 or send an enquiry with what you need, and we will share the current price and suitable options.",
    },
    {
        q: "Do you provide installation?",
        a: "Yes. We provide professional assistance for relevant products and setups — including CCTV installation and complete solar panel setup. Tell us your requirement and we will guide you.",
    },
    {
        q: "Which areas do you serve?",
        a: "We are located at Paikpara, Beniagram in Murshidabad, West Bengal. Call us to check product availability and service options for your area.",
    },
    {
        q: "Do you serve both homes and businesses?",
        a: "Yes. We provide home and commercial power backup, home and business CCTV surveillance, and home and business broadband solutions.",
    },
    {
        q: "Which brands do you carry?",
        a: "We are an authorised distributor for Smarten and also carry INTU lithium batteries, Exide and Amaron batteries, and more. Contact us for the current range in stock.",
    },
];

export default function FAQ() {
    const { t } = useLang();
    const [open, setOpen] = useState(0);

    return (
        <section id="faq" data-testid="faq-section" className="relative py-24 md:py-32 bg-surface border-t border-ink/5">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
                <Reveal className="text-center flex flex-col items-center">
                    <Eyebrow>{t.faq.eyebrow}</Eyebrow>
                    <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-ink mt-4">
                        {t.faq.headingA}<span className="text-cyan2">{t.faq.headingB}</span>
                    </h2>
                </Reveal>

                <div className="mt-12 space-y-4">
                    {t.faq.items.map((f, i) => {
                        const isOpen = open === i;
                        return (
                            <Reveal key={f.q} delay={i * 0.06}>
                                <div
                                    data-testid={`faq-item-${i}`}
                                    className={`rounded-2xl border transition-colors duration-300 ${
                                        isOpen ? "border-cyan2/35 bg-navy/70" : "border-ink/8 bg-navy/40"
                                    }`}
                                >
                                    <button
                                        data-testid={`faq-question-${i}`}
                                        onClick={() => setOpen(isOpen ? -1 : i)}
                                        aria-expanded={isOpen}
                                        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                                    >
                                        <span className="font-heading text-base md:text-lg font-semibold text-ink">
                                            {f.q}
                                        </span>
                                        <motion.span
                                            animate={{ rotate: isOpen ? 180 : 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="shrink-0 text-cyan2"
                                        >
                                            <Icon name="chevron" size={18} />
                                        </motion.span>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.35, ease: EASE }}
                                                className="overflow-hidden"
                                            >
                                                <p
                                                    data-testid={`faq-answer-${i}`}
                                                    className="px-6 pb-6 text-sm md:text-base text-ice/65 leading-relaxed"
                                                >
                                                    {f.a}
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
